import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of, timer } from 'rxjs';
import { MarketService } from './market.service';
import { map, mergeMap, switchMap, take } from 'rxjs/operators';
import { PaymasterMethod } from '../models/paymaster-method';
import BigNumber from 'bignumber.js';
import { sharePublishReplay, swalloError } from 'ngx-libs';
import { AccountService } from './account.service';
import { EthWalletService } from './eth-wallet.service';
import { environment } from 'src/environments/environment';
import { Token } from '../models/token';
import { ContractService } from './contract.service';
import { HoldstationPaymaster } from '../models/holdstation-paymaster';

@Injectable({
  providedIn: 'root'
})
export class PaymasterService {
  private keySubject = new BehaviorSubject(localStorage.getItem('PAYMASTER_METHOD') || '');
  key = this.keySubject.asObservable();
  item: Observable<any>;
  private items: any[] = [];

  constructor(
    private marketService: MarketService,
    private accountService: AccountService,
    private ethWalletService: EthWalletService,
    private contractService: ContractService
  ) {
    this.items = this.createItems(null);
    localStorage.getItem('PAYMASTER_METHOD') || ''
    this.item = this.key.pipe(
      map((k) => {
        let item = this.items.find((i) => i.key === k);
        if (!item) {
          item = this.items[0];;
          this.select('');
        }
        return item;
      })
    );
  }

  select(key: string) {
    if (this.keySubject.value === key) {
      return false;
    }
    this.keySubject.next(key);
    localStorage.setItem('PAYMASTER_METHOD', key || '');
    return true;
  }

  createItems(method: (paymasterMethod?: PaymasterMethod) => Observable<BigNumber>) {
    const ethPrice = this.marketService.ethMarket.pipe(switchMap((market) => market.spotPricer.price));
    const ethItem = {
      tokenName: 'Ethereum',
      symbol: of('ETH'),
      gas: timer(0, 60000).pipe(
        switchMap(() => method()),
        sharePublishReplay(1)
      ),
      quoteGas: of(new BigNumber(0)),
      balance: combineLatest([this.accountService.account, this.ethWalletService.blockNumber]).pipe(
        switchMap(([a]) =>
          a
            ? this.ethWalletService.getBalance(a).pipe(
                swalloError(),
                map((v) => new BigNumber(v).fromDecimal())
              )
            : of(new BigNumber(0))
        ),
        sharePublishReplay(1)
      ),
      precision: 6,
      icon: '/retired-arbitrum/assets/images/eth.svg',
      key: ''
    };
    const items = [ethItem];
    environment.paymasters.forEach((p) => {
      p.tokens.forEach((t: any) => {
        let tokenPrice;
        if (t.priceMethod === 'eth') {
          tokenPrice = ethPrice;
        } else {
          tokenPrice = of(new BigNumber(0));
        }

        const token = new Token(
          t.address,
          t.decimal,
          this.accountService,
          this.ethWalletService,
          this.contractService
        );

        const data = p.type === 'holdstation' ? new HoldstationPaymaster(p.address, this.accountService, this.contractService) : null;

        const item: any = {
          paymaster: p.address,
          provider: p.name,
          paymasterType: p.type,
          data: data,
          tokenName: t.name,
          token,
          tokenPrice,
          symbol: token.symbol,
          gas: tokenPrice.pipe(
            switchMap((price) =>
              method({
                paymaster: p.address,
                token,
                tokenPrice: price,
                type: p.type,
                name: p.name,
                tokenName: t.name,
                data
              })
            ),
            sharePublishReplay(1)
          ),
          balance: token.balance,
          precision: t.precision,
          icon: t.icon,
          discount: t.discount,
          key: p.address + t.address
        };

        item.quoteGas = combineLatest([item.gas as Observable<BigNumber>, this.marketService.ethMarket]).pipe(
          map(([gas, market]) => (market.collateral.contract === token.contract ? gas : new BigNumber(0)))
        );

        items.push(item);
      });
    });

    return items;
  }

  getMethod() {
    return this.item.pipe(
      take(1),
      mergeMap((item) => {
        if (item.token) {
          return item.tokenPrice.pipe(
            take(1),
            map((price) => {
              return {
                paymaster: item.paymaster,
                token: item.token,
                tokenPrice: price,
                type: item.paymasterType,
                name: item.provider,
                tokenName: item.tokenName,
                data: item.data
              };
            })
          );
        } else {
          return of(null);
        }
      })
    ) as Observable<PaymasterMethod>;
  }
}
